import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '@/hooks/useProducts';
import { useAlertCounts, useAlerts } from '@/hooks/useAlerts';
import { useCashRegisterStatus } from '@/hooks/useCashRegister';
import BillingBanner from '@/components/BillingBanner';
import { useAuthStore } from '@/context/authStore';
import StoreOrdersWidget from '@/components/StoreOrdersWidget';
import { ModulePermission } from '@/types';
import {
  Package,
  ShoppingCart,
  AlertTriangle,
  TrendingDown,
  Calendar,
  ArrowRight,
  Activity,
  DollarSign,
  Banknote,
  CreditCard,
  Wallet,
  TrendingUp,
  CheckCircle2,
  XCircle,
  BarChart3,
} from 'lucide-react';
import clsx from 'clsx';

export default function Dashboard() {
  const { user, tenant } = useAuthStore();

  // Obtener permisos efectivos
  const permissions = useMemo(() => {
    return user?.effective_permissions || [];
  }, [user?.effective_permissions]);

  const hasPermission = (permission: ModulePermission): boolean => {
    return permissions.includes(permission);
  };

  // Determinar si es rol de tesorero/cajero
  const isCashierRole = useMemo(() => {
    return user?.role === 'cashier' ||
           (hasPermission('cash_register') && hasPermission('sales') && !hasPermission('products'));
  }, [user?.role, permissions]);

  // Solo cargar datos si el usuario tiene permiso
  const { data: products } = useProducts({}, { enabled: hasPermission('products') });
  const { data: lowStockProducts } = useProducts({ only_low_stock: true }, { enabled: hasPermission('products_write') });
  const { data: alertCounts } = useAlertCounts({ enabled: hasPermission('alerts') });
  const { data: recentAlerts } = useAlerts({ limit: 5, enabled: hasPermission('alerts') });
  const { data: cashStatus } = useCashRegisterStatus({ enabled: hasPermission('cash_register') });

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const alertTypeIcons: Record<string, typeof TrendingDown> = {
    low_stock: TrendingDown,
    expiration: Calendar,
  };

  const alertTypeColors: Record<string, string> = {
    low_stock: 'text-cream-700 bg-cream-100',
    expiration: 'text-rose-600 bg-rose-50',
  };

  const alertTypeLabels: Record<string, string> = {
    low_stock: 'Stock bajo',
    expiration: 'Por vencer',
  };

  // Formatear moneda
  const formatCurrency = (value: number | string | undefined) => {
    const num = typeof value === 'string' ? parseFloat(value) : value || 0;
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(num);
  };

  // Si es tesorero/cajero, mostrar dashboard especializado
  if (isCashierRole) {
    return <CashierDashboard
      user={user}
      tenant={tenant}
      cashStatus={cashStatus}
      formatCurrency={formatCurrency}
      getGreeting={getGreeting}
      hasPermission={hasPermission}
    />;
  }

  // Dashboard Admin/General
  return (
    <div className="space-y-6">
      {/* Billing Banner (solo admin) */}
      {user?.role === 'admin' && <BillingBanner />}

      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-taupe-600 via-taupe-700 to-taupe-800 rounded-2xl p-8">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-64 h-64 bg-cream-400/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-cream-300/10 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4"></div>
        </div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
              <span className="text-cream-300 text-sm font-medium">{tenant?.name || ''}</span>
            </div>
            <h1 className="text-3xl font-bold text-cream-50">
              {getGreeting()}, {user?.full_name?.split(' ')[0]}
            </h1>
            <p className="text-taupe-200 mt-1">
              Panel de administración
            </p>
          </div>
          <div className="flex items-center gap-3">
            {hasPermission('sales') && (
              <Link
                to="/dashboard/sales?tab=new"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-cream-100 text-taupe-800 rounded-xl font-medium hover:bg-white transition-all shadow-lg hover:shadow-xl"
              >
                <ShoppingCart className="h-4 w-4" />
                Nueva Venta
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Tienda online: link + pedidos (si está activa y tiene permiso de ventas) */}
      {hasPermission('sales') && <StoreOrdersWidget tenant={tenant} />}

      {/* Banner Caja Cerrada */}
      {hasPermission('cash_register') && cashStatus && !cashStatus.is_open && (
        <Link
          to="/dashboard/cash-register"
          className="block bg-gradient-to-r from-cream-100 to-cream-200 rounded-2xl p-6 shadow-sm hover:shadow-md border border-cream-300 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
                <Banknote className="h-7 w-7 text-taupe-700" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-taupe-900">La caja está cerrada</h3>
                <p className="text-sm text-taupe-600">Haz clic aquí para abrir la caja del día</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-taupe-800 hover:bg-taupe-900 text-white px-5 py-3 rounded-xl transition-all shadow-sm">
              <span className="font-semibold">Abrir Caja</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </Link>
      )}

      {/* Caja abierta - resumen rápido */}
      {hasPermission('cash_register') && cashStatus?.is_open && cashStatus.current_register && (
        <Link
          to="/dashboard/cash-register"
          className="block bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                <Banknote className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-emerald-100 text-sm">Caja abierta</p>
                <p className="text-white font-bold">
                  Ventas: {formatCurrency(cashStatus.current_register.total_sales)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-white/90 text-sm">
              <div className="text-center">
                <p className="text-emerald-200 text-xs">Efectivo</p>
                <p className="font-medium">{formatCurrency(cashStatus.current_register.total_cash_sales)}</p>
              </div>
              <div className="text-center">
                <p className="text-emerald-200 text-xs">Tarjeta</p>
                <p className="font-medium">{formatCurrency(cashStatus.current_register.total_card_sales)}</p>
              </div>
              <div className="text-center">
                <p className="text-emerald-200 text-xs">Transferencia</p>
                <p className="font-medium">{formatCurrency(cashStatus.current_register.total_transfer_sales)}</p>
              </div>
              <div className="text-center">
                <p className="text-emerald-200 text-xs">QR</p>
                <p className="font-medium">{formatCurrency(cashStatus.current_register.total_qr_sales)}</p>
              </div>
              <ArrowRight className="h-5 w-5 text-white/70 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </Link>
      )}

      {/* Stats principales */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {hasPermission('products') && (
          <Link to="/dashboard/products" className="group bg-white rounded-2xl p-5 border border-cream-200 hover:border-taupe-300 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className="w-11 h-11 bg-gradient-to-br from-taupe-500 to-taupe-700 rounded-xl flex items-center justify-center shadow-md">
                <Package className="h-5 w-5 text-white" />
              </div>
              <ArrowRight className="h-4 w-4 text-taupe-300 group-hover:text-taupe-500 group-hover:translate-x-1 transition-all" />
            </div>
            <p className="text-2xl font-bold text-taupe-800">{products?.length || 0}</p>
            <p className="text-sm text-taupe-500">Productos</p>
          </Link>
        )}

        {hasPermission('alerts') && (
          <Link to="/dashboard/alerts" className="group bg-white rounded-2xl p-5 border border-cream-200 hover:border-taupe-300 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className={clsx(
                'w-11 h-11 rounded-xl flex items-center justify-center shadow-md',
                (alertCounts?.total || 0) > 0
                  ? 'bg-gradient-to-br from-rose-500 to-rose-600'
                  : 'bg-gradient-to-br from-taupe-400 to-taupe-500'
              )}>
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <ArrowRight className="h-4 w-4 text-taupe-300 group-hover:text-taupe-500 group-hover:translate-x-1 transition-all" />
            </div>
            <p className="text-2xl font-bold text-taupe-800">{alertCounts?.total || 0}</p>
            <p className="text-sm text-taupe-500">Alertas</p>
          </Link>
        )}

        {hasPermission('products_write') && (
          <Link to="/dashboard/inventory" className="group bg-white rounded-2xl p-5 border border-cream-200 hover:border-taupe-300 hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className="w-11 h-11 bg-cream-100 rounded-xl flex items-center justify-center">
                <TrendingDown className="h-5 w-5 text-cream-700" />
              </div>
              <ArrowRight className="h-4 w-4 text-taupe-300 group-hover:text-taupe-500 group-hover:translate-x-1 transition-all" />
            </div>
            <p className="text-2xl font-bold text-taupe-800">{lowStockProducts?.length || 0}</p>
            <p className="text-sm text-taupe-500">Stock bajo</p>
          </Link>
        )}
      </div>

      {/* Contenido principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Alertas - lado izquierdo */}
        {hasPermission('alerts') && (
          <div className="lg:col-span-2 bg-white rounded-2xl border border-cream-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-cream-100 flex items-center justify-between bg-cream-50/50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-rose-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-rose-600" />
                </div>
                <h2 className="font-semibold text-taupe-800">Alertas Recientes</h2>
              </div>
              <Link to="/dashboard/alerts" className="text-sm text-taupe-600 hover:text-taupe-800 font-medium">
                Ver todas →
              </Link>
            </div>

            <div className="p-4">
              {recentAlerts && recentAlerts.length > 0 ? (
                <div className="space-y-3">
                  {recentAlerts.map((alert) => {
                    const Icon = alertTypeIcons[alert.alert_type];
                    const colorClass = alertTypeColors[alert.alert_type];
                    return (
                      <div
                        key={alert.id}
                        className={clsx(
                          'flex items-start gap-4 p-4 rounded-xl transition-colors',
                          alert.is_read ? 'bg-cream-50' : 'bg-white border border-cream-200 shadow-sm'
                        )}
                      >
                        <div className={clsx('p-2.5 rounded-xl', colorClass)}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-taupe-800">{alert.title}</p>
                            <span className={clsx('text-xs px-2 py-0.5 rounded-full flex-shrink-0', colorClass)}>
                              {alertTypeLabels[alert.alert_type]}
                            </span>
                          </div>
                          {alert.message && (
                            <p className="text-sm text-taupe-500 mt-1 line-clamp-1">{alert.message}</p>
                          )}
                        </div>
                        {!alert.is_read && (
                          <span className="w-2.5 h-2.5 bg-taupe-500 rounded-full flex-shrink-0 mt-2" />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-cream-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Activity className="h-8 w-8 text-taupe-400" />
                  </div>
                  <p className="text-taupe-600 font-medium">Sin alertas pendientes</p>
                  <p className="text-sm text-taupe-400 mt-1">Todo está bajo control</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Panel lateral derecho */}
        <div className={clsx('space-y-5', !hasPermission('alerts') && 'lg:col-span-2')}>
          {/* Stock bajo */}
          {hasPermission('products_write') && (lowStockProducts?.length || 0) > 0 && (
            <div className="bg-cream-50 rounded-2xl p-5 border border-cream-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-cream-100 rounded-xl flex items-center justify-center">
                  <TrendingDown className="h-5 w-5 text-cream-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-taupe-800">Stock Bajo</h3>
                  <p className="text-sm text-cream-700">{lowStockProducts?.length} productos</p>
                </div>
              </div>
              <Link
                to="/dashboard/inventory"
                className="inline-flex items-center gap-2 text-sm font-medium text-taupe-700 hover:text-taupe-900"
              >
                Ver inventario
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {/* Acciones rápidas */}
          <div className="bg-white rounded-2xl border border-cream-200 p-5">
            <h3 className="font-semibold text-taupe-800 mb-4">Acciones Rápidas</h3>
            <div className="grid grid-cols-2 gap-3">
              {hasPermission('sales') && (
                <Link
                  to="/dashboard/sales?tab=new"
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-cream-50 hover:bg-cream-100 transition-colors group"
                >
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <DollarSign className="h-5 w-5 text-emerald-600" />
                  </div>
                  <span className="text-sm font-medium text-taupe-700">Nueva Venta</span>
                </Link>
              )}

              {hasPermission('products_write') && (
                <Link
                  to="/dashboard/inventory"
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-cream-50 hover:bg-cream-100 transition-colors group"
                >
                  <div className="w-10 h-10 bg-taupe-100 rounded-xl flex items-center justify-center group-hover:bg-taupe-200 transition-colors">
                    <Package className="h-5 w-5 text-taupe-700" />
                  </div>
                  <span className="text-sm font-medium text-taupe-700">Inventario</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Dashboard especializado para Tesorero/Cajero
interface CashierDashboardProps {
  user: any;
  tenant: any;
  cashStatus: any;
  formatCurrency: (value: number | string | undefined) => string;
  getGreeting: () => string;
  hasPermission: (permission: ModulePermission) => boolean;
}

function CashierDashboard({ user, tenant, cashStatus, formatCurrency, getGreeting, hasPermission }: CashierDashboardProps) {
  const currentRegister = cashStatus?.current_register;
  const isOpen = cashStatus?.is_open;

  return (
    <div className="space-y-6">
      {/* Header estilo tesorero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-taupe-700 via-taupe-800 to-taupe-900 rounded-2xl p-8">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-64 h-64 bg-cream-400/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-cream-400/10 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4"></div>
        </div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className={clsx(
                'w-3 h-3 rounded-full animate-pulse',
                isOpen ? 'bg-emerald-400' : 'bg-rose-400'
              )}></div>
              <span className="text-cream-300 text-sm font-medium">
                {isOpen ? 'Caja Abierta' : 'Caja Cerrada'}
              </span>
            </div>
            <h1 className="text-3xl font-bold text-cream-50">
              {getGreeting()}, {user?.full_name?.split(' ')[0]}
            </h1>
            <p className="text-taupe-300 mt-1">
              Panel de Tesorería · {tenant?.name || ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard/cash-register"
              className={clsx(
                'inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all shadow-lg hover:shadow-xl',
                isOpen
                  ? 'bg-rose-500 hover:bg-rose-600 text-white'
                  : 'bg-emerald-500 hover:bg-emerald-600 text-white'
              )}
            >
              <Banknote className="h-4 w-4" />
              {isOpen ? 'Cerrar Caja' : 'Abrir Caja'}
            </Link>
            {hasPermission('sales') && (
              <Link
                to="/dashboard/sales?tab=new"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-cream-100 text-taupe-800 rounded-xl font-medium hover:bg-white transition-all shadow-lg hover:shadow-xl"
              >
                <ShoppingCart className="h-4 w-4" />
                Nueva Venta
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Estado de caja */}
      {isOpen && currentRegister && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-5 border border-cream-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                <Wallet className="h-5 w-5 text-white" />
              </div>
              <span className="text-sm text-taupe-500">Efectivo Inicial</span>
            </div>
            <p className="text-xl font-bold text-taupe-800">{formatCurrency(currentRegister.opening_cash)}</p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-cream-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-taupe-500 to-taupe-700 rounded-xl flex items-center justify-center shadow-md">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <span className="text-sm text-taupe-500">Ventas del Día</span>
            </div>
            <p className="text-xl font-bold text-taupe-800">{formatCurrency(currentRegister.total_sales)}</p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-cream-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                <Banknote className="h-5 w-5 text-white" />
              </div>
              <span className="text-sm text-taupe-500">Ventas Efectivo</span>
            </div>
            <p className="text-xl font-bold text-taupe-800">{formatCurrency(currentRegister.total_cash_sales)}</p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-cream-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cream-500 to-cream-700 rounded-xl flex items-center justify-center shadow-md">
                <CreditCard className="h-5 w-5 text-white" />
              </div>
              <span className="text-sm text-taupe-500">Tarjeta + Transf + QR</span>
            </div>
            <p className="text-xl font-bold text-taupe-800">
              {formatCurrency((parseFloat(currentRegister.total_card_sales || '0') + parseFloat(currentRegister.total_transfer_sales || '0') + parseFloat(currentRegister.total_qr_sales || '0')))}
            </p>
          </div>
        </div>
      )}

      {/* Caja cerrada - mostrar mensaje */}
      {!isOpen && (
        <div className="bg-cream-50 rounded-2xl p-8 border border-cream-200 text-center">
          <div className="w-16 h-16 bg-cream-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <XCircle className="h-8 w-8 text-cream-700" />
          </div>
          <h3 className="text-xl font-bold text-taupe-800 mb-2">Caja Cerrada</h3>
          <p className="text-taupe-600 mb-4">Debe abrir la caja para comenzar a registrar ventas</p>
          <Link
            to="/dashboard/cash-register"
            className="inline-flex items-center gap-2 px-6 py-3 bg-taupe-800 text-white rounded-xl font-medium hover:bg-taupe-900 transition-all shadow-sm"
          >
            <Banknote className="h-5 w-5" />
            Abrir Caja
          </Link>
        </div>
      )}

      {/* Acciones rápidas */}
      <div className="bg-white rounded-2xl border border-cream-200 p-6">
        <h3 className="font-semibold text-taupe-800 mb-5">Acciones Rápidas</h3>
        <div className="space-y-3">
          {hasPermission('sales') && (
            <Link
              to="/dashboard/sales?tab=new"
              className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-emerald-100/50 hover:from-emerald-100 hover:to-emerald-100 transition-colors group border border-emerald-200"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                <DollarSign className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-taupe-800">Nueva Venta</p>
                <p className="text-sm text-taupe-500">Registrar venta directa</p>
              </div>
              <ArrowRight className="h-5 w-5 text-emerald-500 group-hover:translate-x-1 transition-transform" />
            </Link>
          )}

          {hasPermission('cash_register') && (
            <Link
              to="/dashboard/cash-register"
              className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-cream-50 to-cream-100/50 hover:from-cream-100 hover:to-cream-100 transition-colors group border border-cream-200"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-taupe-500 to-taupe-700 rounded-xl flex items-center justify-center shadow-md">
                <BarChart3 className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-taupe-800">Ver Caja</p>
                <p className="text-sm text-taupe-500">Resumen y cierre de caja</p>
              </div>
              <ArrowRight className="h-5 w-5 text-taupe-500 group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
        </div>
      </div>

      {/* Indicador del usuario */}
      {currentRegister && (
        <div className="bg-white rounded-2xl border border-cream-200 p-5 flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <p className="text-sm text-taupe-600">Sesión activa con caja abierta</p>
        </div>
      )}
    </div>
  );
}
